/**
 * Character Counter Utility
 *
 * Provides functions to count visible text characters in HTML content,
 * excluding HTML tags, image tags, and image URLs for the Further Reading field.
 */

/**
 * Decodes HTML entities to their actual characters.
 *
 * @param {string} text - Text with HTML entities
 * @returns {string} Text with decoded entities
 */
const decodeHTMLEntities = (text) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

/**
 * Removes all HTML tags from a string.
 *
 * @param {string} text - HTML string to clean
 * @returns {string} Plain text without HTML tags
 */
const removeAllHTMLTags = (text) => text.replace(/<[^>]*>/g, '').trim();

/**
 * Normalizes Quill formula nodes by replacing the generated KaTeX HTML with
 * the original formula value stored in data-value.
 *
 * @param {Document} doc - Parsed HTML document
 */
const normalizeFormulaNodes = (doc) => {
  doc.querySelectorAll('.ql-formula').forEach((formulaNode) => {
    const formulaValue = formulaNode.getAttribute('data-value') || '';
    formulaNode.replaceWith(doc.createTextNode(formulaValue));
  });
};

/**
 * Removes image nodes from parsed HTML content.
 *
 * @param {Document} doc - Parsed HTML document
 */
const removeImageNodes = (doc) => {
  doc.querySelectorAll('img').forEach((img) => img.remove());
};

/**
 * Removes links that point directly to image files.
 *
 * @param {Document} doc - Parsed HTML document
 */
const removeImageLinks = (doc) => {
  doc.querySelectorAll('a[href]').forEach((link) => {
    const href = link.getAttribute('href') || '';

    if (/\.(jpg|jpeg|png|gif|bmp|svg|webp)(\?.*)?$/i.test(href)) {
      link.remove();
    }
  });
};

/**
 * Removes standalone image URLs from plain text.
 *
 * @param {string} text - Plain text content
 * @returns {string} Plain text without standalone image URLs
 */
const removeStandaloneImageUrls = (text) =>
  text.replace(
    /https?:\/\/[^\s<>"]+\.(jpg|jpeg|png|gif|bmp|svg|webp)(\?[^\s<>"]*)?/gi,
    '',
  );

/**
 * Normalizes invisible characters and whitespace.
 *
 * @param {string} text - Plain text content
 * @returns {string} Normalized plain text
 */
const normalizeWhitespace = (text) =>
  text
    .replace(/\uFEFF/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Counts the number of visible text characters in HTML content,
 * excluding HTML tags, image tags, and image URLs.
 *
 * @param {string} htmlContent - HTML string to count characters from
 * @returns {number} Character count of visible text only
 *
 * @example
 * const count = getTextCharacterCount('<p>Hello World</p>');
 * // Returns: 11 (only "Hello World")
 *
 * const countWithImage = getTextCharacterCount('<p>Text</p><img src="test.jpg" />');
 * // Returns: 4 (only "Text", image is excluded)
 */
export const getTextCharacterCount = (htmlContent) => {
  if (!htmlContent) return 0;

  try {
    // First, decode HTML entities (&lt; → <, &gt; → >, etc.)
    const cleanedContent = decodeHTMLEntities(htmlContent);

    const parser = new DOMParser();
    const doc = parser.parseFromString(cleanedContent, 'text/html');

    // Replace Quill/KaTeX formulas with only their original formula value.
    // This avoids counting the large internal KaTeX HTML multiple times.
    normalizeFormulaNodes(doc);

    // Remove <img> tags completely (including attributes)
    removeImageNodes(doc);

    // Remove <a> tags that link to images (remove the entire link)
    removeImageLinks(doc);

    // Remove standalone image URLs (that appear as plain text)
    const textWithoutImageUrls = removeStandaloneImageUrls(
      doc.body.textContent || '',
    );

    // Remove all HTML tags
    const plainText = removeAllHTMLTags(textWithoutImageUrls);

    // Return the length of plain text
    return normalizeWhitespace(plainText).length;
  } catch (error) {
    // If there's any error, return 0
    return 0;
  }
};

/**
 * Validates if the HTML content exceeds the maximum character limit.
 *
 * @param {string} htmlContent - HTML string to validate
 * @param {number} maxChars - Maximum allowed characters (default: 1000)
 * @returns {boolean} True if valid (within limit), false if exceeds limit
 *
 * @example
 * const isValid = validateCharacterLimit('<p>Short text</p>');
 * // Returns: true
 *
 * const tooLong = validateCharacterLimit('<p>Very long text...</p>', 10);
 * // Returns: false
 */
export const validateCharacterLimit = (htmlContent, maxChars = 1000) => {
  const charCount = getTextCharacterCount(htmlContent);
  return charCount <= maxChars;
};

/**
 * Gets character count status for styling purposes.
 * Provides count and validity based on the character count.
 *
 * @param {string} htmlContent - HTML string to check
 * @param {number} maxChars - Maximum allowed characters (default: 1000)
 * @returns {object} Object with count and isValid properties
 *
 * @example
 * const status = getCharacterStatus('<p>Some text</p>');
 * // Returns: { count: 9, isValid: true }
 *
 * const overLimit = getCharacterStatus('<p>Text with 1100 chars...</p>');
 * // Returns: { count: 1100, isValid: false }
 */
export const getCharacterStatus = (htmlContent, maxChars = 1000) => {
  const count = getTextCharacterCount(htmlContent);

  return {
    count,
    isValid: count <= maxChars,
  };
};
